import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Filters } from 'hooks/store/temperature-control/use-equipment-search-params';
import {
  useCompanyOptions,
} from 'hooks/api/options';
import { Form, FormField } from 'components/ui/form';
import { InputGs, MultiSelectGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';

import { formSchema, type FormSchema } from './form-schema';
import { createFilters, createReportParams } from './utils';

type Ref = {
  getFormData: () => Record<string, unknown>;
  resetFilters: () => void;
}

type Props = {
  onSubmitFilters: (filters: Filters) => void;
  onResetFilters: () => void;
}

export type EquipmentFiltersRef = Ref;

const SELECTED_BADGE_CLASS_NAME =
  'md:max-w-[200px] lg:max-w-[180px] xl:max-w-[150px]';

export const EquipmentFilters = React.forwardRef<Ref, Props>(({
                                                                onSubmitFilters,
                                                                onResetFilters,
                                                              }, ref) => {

  const companyOptions = useCompanyOptions({
    labelType: 'sap-code-trade-name-and-document',
    onlyWithSapCode: true,
  });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const resetForm = React.useCallback(() => {
    form.reset({
      equipmentCode: '',
      containerCode: '',
      companyId: [],
    });
    onResetFilters();
  }, [form, onResetFilters]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    const filters = createFilters(formData);
    return onSubmitFilters(filters);
  }, [onSubmitFilters]);

  const getFormData = React.useCallback(() => {
    const formData = form.getValues();
    return createReportParams(formData);
  }, [form]);

  React.useImperativeHandle(ref, () => ({
    resetFilters: resetForm,
    getFormData,
  }), [resetForm, getFormData]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className={'w-full'}>
        <div className={'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4'}>
          <FormField
            control={form.control}
            name={'equipmentCode'}
            render={({ field }) => (
              <InputGs {...field} type="text" label={'Cód. equipamento'}
                       placeholder={'códigos separados por vírgula'}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'containerCode'}
            render={({ field }) => (
              <InputGs {...field} type="text" label={'Cód. container'}
                       placeholder={'códigos separados por vírgula'}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'companyId'}
            render={({ field }) => (
              <MultiSelectGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                label={'Filial'}
                maxCount={1}
                options={companyOptions.multiSelectOptions}
                classNames={{
                  selectedBadge: SELECTED_BADGE_CLASS_NAME,
                }}
              />
            )}
          />
        </div>
        <div className={'flex items-center mt-6 gap-4'}>
          <Button.Root
            type={'button'}
            size={'sm'}
            variant={'secondary'}
            onClick={resetForm}
          >
            Limpar filtros
          </Button.Root>
          <Button.Root
            type={'submit'}
            size={'sm'}
            variant={'primary'}
          >
            Filtrar
          </Button.Root>
        </div>
      </form>
    </Form>
  );
});
EquipmentFilters.displayName = 'EquipmentFilters';